import { logPrefix } from './constants';
import Identity from './identity';

type ThirdPartyService = 'DISQUS'; // | 'TWITTER'

interface DisqusTokens {
  publicKey?: string;
  ssoToken?: string;
  enabled: boolean;
}

/**
 * Gets third party tokens (e.g. SSO tokens for Disqus) for use within the UI.  This method combines any user settings
 * and config options required by the third party service to sign on.
 *
 * Current tokens supported are:
 *  - 'DISQUS'
 *
 * @returns
 * A __Promise__ which resolves to a JSON object with SSO token and public key for Disqus
 *
 * <details>
 * <summary>
 * On success (resolves)
 * </summary>
 *
 * ```json
 *
 * ```
 * </details>
 *
 * <details>
 * <summary>
 * On errors (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/User_Settings/getSettings
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#operations-Tenant%20Configuration-Get-tenant-configuration
 * ```
 * </details>
 **/
export async function getThirdPartyTokens(service: ThirdPartyService): Promise<DisqusTokens> {
  if (service === 'DISQUS') {
    return getDisqusData();
  } else {
    throw new Error(`${logPrefix} Unsupported third party service requested.`);
  }
}

/**
 * Combines the ssoToken from local storage and the publicKey + enabled boolean from configOptions.
 *
 * @hidden
 */
async function getDisqusData(): Promise<DisqusTokens> {
  if (!Identity.userSettings) {
    await Identity.getUserSettings();
  }

  if (!Identity.configOptions) {
    await Identity.getConfig();
  }

  if (
    Identity.configOptions &&
    Identity.userSettings &&
    Identity.configOptions.disqus &&
    Identity.configOptions.disqus.enabled
  ) {
    return {
      enabled: true,
      publicKey: Identity.configOptions.disqus.publicKey,
      ssoToken: Identity.userSettings.disqus && Identity.userSettings.disqus.ssoKey
    };
  } else {
    return {
      enabled: false
    };
  }
}

export default getThirdPartyTokens;
